<template>
  <!-- job: Position Calmren as Cal's consultancy: judgement, implementation,
       handover, and light support for small organisations. -->
  <div class="home-page">

    <!-- 1. Hero -->
    <Section width="wide" padding="loose">
      <div class="home-hero-grid">
        <div>
          <Eyebrow tone="clay" class="mb-8">Calmren · Cal Chamberlain</Eyebrow>
          <h1 class="text-page-title text-ink mb-10">
            Calmren
          </h1>
          <p class="text-xl md:text-2xl text-body leading-snug max-w-2xl mb-6">
            Practical tech help for solo operators, not-for-profits, and small teams in New Zealand.
          </p>
          <p class="text-base md:text-lg text-body leading-relaxed max-w-2xl mb-3">
            I map the work, tidy the tools, build the missing pieces, and leave you able to run the thing yourself. AI comes in when it improves the process, not because it looks clever in a proposal.
          </p>
          <p class="text-base text-muted leading-relaxed max-w-2xl">
            Bespoke work starts here. Named AI products live at Sensible AI.
          </p>
        </div>

        <aside class="border-y border-rule py-6 lg:py-8">
          <p class="font-mono text-xs uppercase tracking-widest text-muted mb-6">
            A typical engagement
          </p>
          <ol class="space-y-5">
            <li
              v-for="signal in engagementSignals"
              :key="signal.label"
              class="flex gap-4"
            >
              <span class="font-mono text-xs uppercase tracking-widest text-clay-700 pt-1 w-14 shrink-0">
                {{ signal.label }}
              </span>
              <span class="text-sm md:text-base text-body leading-relaxed">
                {{ signal.body }}
              </span>
            </li>
          </ol>
        </aside>
      </div>
    </Section>

    <!-- 2. How I work -->
    <Section width="default" padding="loose" surface="alt">
      <Eyebrow class="mb-6">How I work</Eyebrow>
      <h2 class="text-section-title text-ink mb-12 max-w-2xl">
        Clear scope. Useful handover. No theatre.
      </h2>

      <div class="space-y-10">
        <article
          v-for="shape in shapes"
          :key="shape.title"
          class="engagement-shape gap-3 md:gap-10"
        >
          <h3 class="text-base font-bold text-ink leading-snug pt-1">
            {{ shape.title }}
          </h3>
          <p class="text-body leading-relaxed max-w-2xl">
            {{ shape.body }}
          </p>
        </article>
      </div>

      <p class="mt-12 text-sm text-muted leading-relaxed max-w-2xl">
        Pricing belongs in the proposal, once the shape is real. I work with small budgets often enough to be direct about what is worth doing now and what can wait.
      </p>
    </Section>

    <!-- 3. Vignette -->
    <Section width="default" padding="loose">
      <Eyebrow class="mb-6">An example</Eyebrow>
      <h2 class="text-section-title text-ink mb-10 max-w-2xl">
        One operator. Too many moving parts.
      </h2>

      <div class="text-body leading-relaxed space-y-6 max-w-2xl">
        <p>
          A solo learning consultant had a healthy business and a noisy stack: workshops, a community space, a paid newsletter, client bookings, three calendars, two CRMs, and a mailing list account she was paying for but rarely opened.
        </p>
        <p class="font-mono text-xs uppercase tracking-widest text-muted pt-4">What we did</p>
        <ul class="space-y-3 list-none pl-0">
          <li class="flex gap-4">
            <span class="text-clay-700 mt-1">·</span>
            <span>Mapped how the bookings, payments, community, and writing actually moved through the week</span>
          </li>
          <li class="flex gap-4">
            <span class="text-clay-700 mt-1">·</span>
            <span>Kept the tools doing real work, retired the duplicates, and wrote down the operating rules in plain English</span>
          </li>
          <li class="flex gap-4">
            <span class="text-clay-700 mt-1">·</span>
            <span>Moved bookings into one calendar, set payments up properly, and made client questions visible instead of buried</span>
          </li>
          <li class="flex gap-4">
            <span class="text-clay-700 mt-1">·</span>
            <span>Showed her where Claude could help with the work she was already doing, especially drafting and planning</span>
          </li>
          <li class="flex gap-4">
            <span class="text-clay-700 mt-1">·</span>
            <span>Left her with the accounts, the documentation, and a light support path for questions that come up</span>
          </li>
        </ul>
        <p class="pt-4">
          The old stack was expensive because it demanded attention. The new one was cheaper because it had a job.
        </p>
        <p class="text-ink font-medium">
          Six weeks later, bookings and client questions were no longer living in her head.
        </p>
      </div>
    </Section>

    <!-- 4. When you won't need me -->
    <Section width="default" padding="loose" surface="alt">
      <Eyebrow class="mb-6">When you won't need me</Eyebrow>
      <h2 class="text-section-title text-ink mb-10 max-w-2xl">
        Sometimes the right answer is to learn the thing yourself.
      </h2>
      <div class="text-body leading-relaxed space-y-5 max-w-2xl">
        <p>
          A recent example: a solo operator I respect was frustrated with her community platform. I could have fixed it. The better move was for her to learn the platform properly, with Claude helping her edit her own setup. If a job is teachable in an afternoon, you should not be paying me a retainer to keep doing it.
        </p>
        <p class="text-ink font-medium">
          Good implementation should make you more capable than when we started.
        </p>
      </div>
    </Section>

    <!-- 5. Network -->
    <Section width="default" padding="normal">
      <Eyebrow class="mb-6">Who I work with</Eyebrow>
      <h2 class="text-section-title text-ink mb-8 max-w-2xl">
        Small network, named when it matters.
      </h2>
      <div class="text-body leading-relaxed space-y-5 max-w-2xl">
        <p>
          Most work is Cal-led. When a job needs admin, design, or product engineering hands, I bring in people I trust and name the shape honestly. No fake agency layer, no mystery team behind the curtain.
        </p>
      </div>
    </Section>

    <!-- 6. Field notes -->
    <Section width="default" padding="normal" divider="top">
      <div class="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div class="max-w-xl">
          <Eyebrow class="mb-6">Field notes</Eyebrow>
          <h2 class="text-section-title text-ink mb-4">
            Working notes from real engagements.
          </h2>
          <p class="text-body leading-relaxed">
            Carousels republished from LinkedIn, longer pieces when something needs more room than a carousel gives.
          </p>
        </div>
        <RouterLink
          to="/field-notes"
          class="font-mono text-xs uppercase tracking-widest text-clay-700 hover:text-clay-800 transition-colors"
        >
          See all field notes →
        </RouterLink>
      </div>

      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <article
          v-for="note in fieldNotePreviews"
          :key="note.title"
          class="aspect-[4/5] bg-surface-card border border-rule p-6 flex flex-col justify-between hover:border-clay-300 transition-colors duration-normal"
        >
          <p class="font-mono text-xs uppercase tracking-widest text-muted">
            {{ note.date }}
          </p>
          <p class="text-base text-ink leading-snug font-medium">
            {{ note.title }}
          </p>
        </article>
      </div>

      <p class="mt-8 text-sm text-muted">
        No email list, no funnel. Follow along on
        <a href="https://www.linkedin.com/in/cal-chamberlain" target="_blank" rel="noopener" class="text-clay-700 hover:text-clay-800 underline-offset-4 hover:underline">LinkedIn</a>
        if you want these as they go up.
      </p>
    </Section>

    <!-- 7. Products I build -->
    <Section width="default" padding="normal" divider="top">
      <Eyebrow class="mb-6">Products I build</Eyebrow>
      <h2 class="text-section-title text-ink mb-8 max-w-2xl">
        Sensible AI is the product house.
      </h2>
      <p class="text-body leading-relaxed max-w-2xl mb-8">
        Calmren is bespoke consulting and implementation. Sensible AI is where the named, repeatable AI products live: <strong class="text-ink font-semibold">Newsren</strong> for media intelligence, <strong class="text-ink font-semibold">Kyne</strong> for member intelligence, and <strong class="text-ink font-semibold">AI Work Partner</strong> for workplace AI setup.
      </p>
      <a
        href="https://sensibleai.nz"
        target="_blank"
        rel="noopener"
        class="font-mono text-xs uppercase tracking-widest text-clay-700 hover:text-clay-800 transition-colors"
      >
        sensibleai.nz →
      </a>
    </Section>

    <!-- 8. Get in touch -->
    <Section width="default" padding="loose" surface="dark">
      <Eyebrow tone="on-dark" class="mb-6">Get in touch</Eyebrow>
      <h2 class="text-section-title text-cream-50 mb-8 max-w-2xl">
        The fastest way is email.
      </h2>
      <a
        href="mailto:cal@calmren.com"
        class="text-2xl md:text-3xl font-bold text-clay-300 hover:text-clay-200 transition-colors duration-normal"
      >
        cal@calmren.com
      </a>
      <p class="text-base text-cream-300 leading-relaxed mt-10 max-w-2xl">
        Or use the <RouterLink to="/contact" class="text-clay-300 hover:text-clay-200 underline-offset-4 hover:underline">contact form</RouterLink>. I'll reply within a working day. If the work is a fit, we'll book half an hour to look at what you've got. If it belongs somewhere else, I'll say so.
      </p>
    </Section>

  </div>
</template>

<script setup lang="ts">
import { RouterLink } from 'vue-router'
import Section from '@/components/marketing/Section.vue'
import Eyebrow from '@/components/marketing/Eyebrow.vue'

const engagementSignals = [
  {
    label: '01',
    body: 'Find the real process, including the parts living in inboxes, memory, and half-used tools.',
  },
  {
    label: '02',
    body: 'Decide what to keep, retire, automate, or connect. Plain systems first. AI only where it helps.',
  },
  {
    label: '03',
    body: 'Build or tidy the setup, document it, and leave the controls with you.',
  },
]

const shapes = [
  {
    title: 'Diagnose',
    body: 'A short, direct look at how the work really moves. You get a practical write-up: what to keep, what to retire, what is missing, and what not to touch yet.',
  },
  {
    title: 'Tidy',
    body: 'Remote cleanup for the stack you already have. Calendars, inboxes, CRMs, payments, automations, permissions, documentation. The unglamorous work that makes the rest possible.',
  },
  {
    title: 'Build',
    body: 'New workflows, AI assistants, integrations, reporting, or lightweight internal tools. Time-bound, tested with the people who will actually use them.',
  },
  {
    title: 'Handover',
    body: 'Documentation, training, and ownership transfer. You hold the keys. Routine changes should not require me.',
  },
  {
    title: 'Light support',
    body: 'A few hours a month for the questions that come up after, or a single session when you need another set of eyes.',
  },
]

// Field note placeholders. Replace with real data once the pipeline is wired.
const fieldNotePreviews = [
  { date: 'Apr 2026', title: 'Tools are downstream of process.' },
  { date: 'Apr 2026', title: 'When you won\'t need a consultant.' },
  { date: 'Apr 2026', title: 'If you refuse AI on principle, you\'re who I most want using it.' },
]
</script>

<style scoped>
.home-page {
  background: var(--surface-bg);
  overflow-x: clip;
}

.home-page p,
.home-page h1,
.home-page h2,
.home-page h3 {
  max-width: calc(100vw - 3rem);
  overflow-wrap: break-word;
}

.home-hero-grid {
  display: grid;
  gap: var(--space-16);
  align-items: end;
}

.home-hero-grid > * {
  min-width: 0;
}

.engagement-shape {
  display: grid;
}

@media (min-width: 1024px) {
  .home-hero-grid {
    grid-template-columns: minmax(0, 1.1fr) minmax(20rem, 0.9fr);
  }
}

@media (min-width: 768px) {
  .engagement-shape {
    grid-template-columns: 14rem minmax(0, 1fr);
  }
}
</style>
